import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Button, Card, Input, Empty, Spin } from "antd";
import {
  ReadOutlined,
  ApartmentOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  BulbOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import room1 from "../assets/room1.jpg";
import story1 from "../assets/story2.png";
import story2 from "../assets/story2.png";
import feature from "../assets/feature.png";
import CommonHeader from "../components/common/CommonHeader";
import { newsService } from "../lib/services/news_services";

const { Title, Paragraph } = Typography;

const NewsroomPage = () => {
  const [categories, setCategories] = useState([]);
  const [insights, setInsights] = useState([]);
  const [featuredStories, setFeaturedStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);

  // Default category icons
  const categoryIcons = {
    "Market Insights": <ReadOutlined style={{ fontSize: 28, color: "#000" }} />,
    "Company Updates": (
      <ApartmentOutlined style={{ fontSize: 28, color: "#000" }} />
    ),
    "Off Plan Highlights": (
      <HomeOutlined style={{ fontSize: 28, color: "#000" }} />
    ),
    "Verified Properties": (
      <CheckCircleOutlined style={{ fontSize: 28, color: "#000" }} />
    ),
    "Investor Tips": <BulbOutlined style={{ fontSize: 28, color: "#000" }} />,
    "Press Release": (
      <FileTextOutlined style={{ fontSize: 28, color: "#000" }} />
    ),
  };

  // Default images for fallback
  const defaultImages = {
    categories: [
      "https://images.unsplash.com/photo-1581091012184-5c9af55f0e8a",
      "https://images.unsplash.com/photo-1605902711622-cfb43c4437b5",
      "https://images.unsplash.com/photo-1528909514045-2fa4ac7a08ba",
      "https://images.unsplash.com/photo-1493809842364-78817add7ffb",
      "https://images.unsplash.com/photo-1554224154-22dec7ec8818",
      "https://images.unsplash.com/photo-1554774853-d50f4d27c47f",
    ],
    insights: [
      "https://images.unsplash.com/photo-1505739770870-7a3e7e48f57e",
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c",
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c",
      "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2",
    ],
  };

  useEffect(() => {
    fetchNewsData();
  }, []);

  const fetchNewsData = async () => {
    try {
      setLoading(true);

      // Fetch categories (News with type "Categories")
      const categoriesData = await newsService.getNewsByType("Categories");
      setCategories(categoriesData);
      setCategoriesLoading(false);

      // Fetch insights (News with type "News")
      const insightsData = await newsService.getNewsByType("News");
      setInsights(insightsData);
      setInsightsLoading(false);

      // Fetch featured stories (News with type "FeaturedStories")
      const featuredData = await newsService.getNewsByType("FeaturedStories");
      setFeaturedStories(featuredData);
    } catch (error) {
      console.error("Error fetching news data:", error);
      setCategoriesLoading(false);
      setInsightsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // Get category icon based on title
  const getCategoryIcon = (title, index) => {
    return (
      categoryIcons[title] ||
      Object.values(categoryIcons)[index % Object.values(categoryIcons).length]
    );
  };

  // Get fallback image based on type and index
  const getFallbackImage = (type, index) => {
    const imageArray = defaultImages[type] || defaultImages.categories;
    return imageArray[index % imageArray.length];
  };

  // Render categories section
  const renderCategories = () => {
    if (categoriesLoading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      );
    }

    if (categories.length === 0) {
      return (
        <Empty
          description="No categories available at the moment"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary">Check Back Later</Button>
        </Empty>
      );
    }

    return (
      <Row gutter={[24, 24]}>
        {categories.map((item, index) => (
          <Col xs={24} sm={12} md={8} key={item.id || index}>
            <Card
              hoverable
              style={{
                borderRadius: 10,
                overflow: "hidden",
                height: "100%",
              }}
              cover={
                <img
                  alt={item.title}
                  src={item.imageUrl || getFallbackImage("categories", index)}
                  style={{
                    height: 180,
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.src = getFallbackImage("categories", index);
                  }}
                />
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                {getCategoryIcon(item.title, index)}
                <Title
                  level={5}
                  style={{ color: "#000", marginBottom: 0, marginLeft: 12 }}
                >
                  {item.title}
                </Title>
              </div>
              <Paragraph style={{ color: "#555", fontSize: 14 }}>
                {item.description}
              </Paragraph>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Render insights section
  const renderInsights = () => {
    if (insightsLoading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      );
    }

    if (insights.length === 0) {
      return (
        <Empty
          description="No news insights available at the moment"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Paragraph style={{ color: "#555" }}>
            Check back later for the latest market updates and insights.
          </Paragraph>
        </Empty>
      );
    }

    return (
      <Row gutter={[24, 24]}>
        {insights.map((item, index) => (
          <Col xs={24} sm={12} md={6} key={item.id || index}>
            <Card
              hoverable
              style={{ borderRadius: 10, overflow: "hidden" }}
              cover={
                <img
                  src={item.imageUrl || getFallbackImage("insights", index)}
                  alt={item.title}
                  style={{ height: 180, objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = getFallbackImage("insights", index);
                  }}
                />
              }
            >
              <Title level={5} style={{ color: "#000", marginBottom: 6 }}>
                {item.title}
              </Title>
              <Paragraph style={{ color: "#555", fontSize: 13 }}>
                {item.description}
              </Paragraph>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Render featured stories section
  const renderFeaturedStories = () => {
    const mainStory = featuredStories[0];
    const sideStories = featuredStories.slice(1, 3);

    if (featuredStories.length === 0) {
      return (
        <Row gutter={[24, 24]}>
          <Col xs={24} md={14}>
            <Card
              style={{ borderRadius: 10, textAlign: "center", padding: "40px" }}
            >
              <Empty
                description="No featured stories available"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          </Col>
          <Col xs={24} md={10}>
            <Card
              style={{
                borderRadius: 10,
                marginBottom: 20,
                textAlign: "center",
                padding: "20px",
              }}
            >
              <Empty description="Check back for updates" image={false} />
            </Card>
            <Card
              style={{ borderRadius: 10, textAlign: "center", padding: "20px" }}
            >
              <Empty description="More stories coming soon" image={false} />
            </Card>
          </Col>
        </Row>
      );
    }

    return (
      <Row gutter={[24, 24]}>
        <Col xs={24} md={14}>
          <Card
            style={{
              borderRadius: 10,
              overflow: "hidden",
              height: "100%",
            }}
            cover={
              <img
                alt="Featured"
                src={mainStory?.imageUrl || feature}
                style={{ height: 260, objectFit: "cover" }}
                onError={(e) => {
                  e.target.src = feature;
                }}
              />
            }
          >
            <Title level={5} style={{ marginBottom: 10 }}>
              {mainStory?.title || "Featured Story"}
            </Title>
            <Paragraph style={{ color: "#555" }}>
              {mainStory?.description ||
                "Check back soon for our latest featured stories."}
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={10}>
          {sideStories.map((story, index) => (
            <Card
              key={story.id || index}
              style={{ borderRadius: 10, marginBottom: 20 }}
              cover={
                <img
                  alt={story.title}
                  src={story.imageUrl || (index === 0 ? story1 : story2)}
                  style={{ height: 120, objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = index === 0 ? story1 : story2;
                  }}
                />
              }
            >
              <Title level={5}>{story.title}</Title>
              <Paragraph style={{ color: "#555", fontSize: 13 }}>
                {story.description}
              </Paragraph>
            </Card>
          ))}

          {/* Fill remaining slots if less than 2 side stories */}
          {sideStories.length < 2 && (
            <Card
              style={{ borderRadius: 10, textAlign: "center", padding: "20px" }}
            >
              <Empty description="More stories coming soon" image={false} />
            </Card>
          )}
        </Col>
      </Row>
    );
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <CommonHeader bgColor="#DDC7BB" />
        <div style={{ textAlign: "center", padding: "100px" }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 20 }}>
            Loading news content...
          </Paragraph>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#fff" }}>
      <CommonHeader bgColor="#DDC7BB" />

      {/* ----------- Top Section ----------- */}
      <div style={{ padding: "80px 10%", background: "#fff" }}>
        <Row gutter={[40, 40]} align="middle">
          <Col xs={24} md={12}>
            <Title level={2} style={{ color: "#000", marginBottom: 16 }}>
              The NITM Newsroom
            </Title>
            <Paragraph style={{ color: "#555", fontSize: 16, lineHeight: 1.7 }}>
              A hub for property market updates, company news, press releases,
              and expert insights — NotInMarket is an informed, transparent, and
              active voice in Dubai real estate. Timely reporting, verified
              sources, and market context help investors, journalists, and
              industry pros make decisive moves.
            </Paragraph>
            <div style={{ marginTop: 30 }}>
              <Button
                type="primary"
                style={{
                  background: "#ff7a00",
                  border: "none",
                  marginRight: 15,
                  padding: "8px 24px",
                  fontWeight: 500,
                }}
              >
                Subscribe
              </Button>
              <Button
                type="default"
                style={{
                  borderColor: "#000",
                  color: "#000",
                  padding: "8px 24px",
                  fontWeight: 500,
                }}
              >
                Explore Stories
              </Button>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <img
              src={room1}
              alt="Dubai Skyline"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 10,
                boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
              }}
            />
          </Col>
        </Row>
      </div>

      {/* ----------- Categories Section ----------- */}
      <div
        style={{
          background: "#faf6f2",
          padding: "60px 10%",
          borderTop: "1px solid #eee",
        }}
      >
        <Title level={4} style={{ color: "#000", marginBottom: 30 }}>
          Categories
        </Title>
        {renderCategories()}
      </div>

      {/* ----------- Market Insights Section ----------- */}
      <div style={{ padding: "80px 10%", background: "#f9f9f9" }}>
        <Title
          level={3}
          style={{ textAlign: "center", color: "#000", marginBottom: 40 }}
        >
          Market Insights and Headlines
        </Title>
        {renderInsights()}
      </div>

      {/* ----------- Featured Stories Section ----------- */}
      <div
        style={{
          background: "#faf6f2",
          padding: "60px 10%",
          borderTop: "1px solid #eee",
        }}
      >
        <Title level={4} style={{ color: "#000", marginBottom: 30 }}>
          Featured Stories
        </Title>
        {renderFeaturedStories()}
      </div>

      {/* ----------- Subscribe Section ----------- */}
      <div
        style={{
          padding: "80px 10%",
          background: "#fff",
          textAlign: "center",
          borderTop: "1px solid #eee",
        }}
      >
        <Title level={4} style={{ color: "#000" }}>
          Subscribe : The Market Brief
        </Title>
        <Paragraph
          style={{
            maxWidth: 600,
            margin: "0 auto 30px",
            color: "#555",
            fontSize: 15,
          }}
        >
          Subscribe to The Market Brief for weekly insights delivered straight
          to your inbox. Concise market summaries, headlines and expert
          takeaways curated for investors and pros.
        </Paragraph>
        <Input.Search
          placeholder="Enter your Email"
          enterButton="Subscribe"
          size="large"
          style={{
            maxWidth: 400,
            margin: "0 auto",
          }}
          onSearch={(value) => console.log("Subscribed:", value)}
        />
      </div>
    </div>
  );
};

export default NewsroomPage;
